/** Device-list pieces shared by the Devices tab (#310) and the "Company
 *  network" guide's `network.device_list` block (#436): the name, status and
 *  "where" of a device, the Make-static dialog, and the one Make-static call.
 *  Extracted from `pages/network/devices.tsx` so the guide shows the same
 *  rows and makes an address static exactly as the page does. */
import { useEffect, useState } from "react";
import { formatDistanceToNow } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { Cable, Wifi } from "lucide-react";
import { toast } from "sonner";
import { Box, Button, Group, Modal, Radio, Stack, Text, TextInput } from "@mantine/core";
import { tenantApi } from "@/lib/api/client";
import type { NetworkDevice } from "@/lib/api/client";
import { NETWORK_DEVICES_QUERY_KEY } from "@/hooks/use-network-queries";

/** Status-dot palette carried over from the old leases page: yellow while a
 *  change is on its way to the router, red while one is being removed, green
 *  for alive, dimmed for everything unknown. */
export function statusDot(color: string, pulse = false) {
  return (
    <Box
      w={8}
      h={8}
      style={{
        borderRadius: "50%",
        background: color,
        animation: pulse ? "pulse 2s infinite" : undefined,
        flexShrink: 0,
      }}
    />
  );
}

export function deviceStatusDot(device: NetworkDevice) {
  if (device.status === "online") return statusDot("var(--mantine-color-green-5)");
  if (device.status === "offline") return statusDot("var(--mantine-color-dimmed)");
  return statusDot("var(--mantine-color-gray-4)");
}

export function deviceStatusLabel(device: NetworkDevice): string {
  if (device.status === "online") return "Online";
  if (device.status === "never_seen") return "Never seen";
  return device.last_seen
    ? `Offline · ${formatDistanceToNow(new Date(device.last_seen), { addSuffix: true })}`
    : "Offline";
}

export function deviceName(device: NetworkDevice): string {
  return device.hostname || device.vendor || device.mac_address;
}

/** Providers that only RATE the link send a word, not a number — Aruba Instant
 *  On reports "good"/"fair". The backend keeps it as a string in
 *  `signal_rating`, deliberately separate from the dBm `signal`, so a bucket is
 *  never rendered as a measurement nobody took. Show it as sent; do not invent a
 *  scale for it. */
function signalRatingWord(rating: string): string {
  const word = rating.trim();
  if (!word) return "";
  return `${word.charAt(0).toUpperCase()}${word.slice(1)} signal`;
}

function formatSpeed(mbps: number): string {
  return mbps >= 1000 ? `${mbps % 1000 === 0 ? mbps / 1000 : (mbps / 1000).toFixed(1)} Gbps` : `${mbps} Mbps`;
}

/** One line describing WHERE the device is attached — branches on medium so
 *  wireless facts (SSID/band/signal) and wired facts (switch/port/speed)
 *  share a column without permanently empty cells. */
export function whereText(device: NetworkDevice): string | null {
  const c = device.connection;
  if (!c || !c.type) return null;
  if (c.type === "wireless") {
    const parts = [c.ssid, c.band].filter(Boolean) as string[];
    if (c.signal != null) parts.push(`${c.signal} dBm`);
    else if (c.signal_rating) parts.push(signalRatingWord(c.signal_rating));
    return parts.join(" · ") || null;
  }
  const parts = [c.switch_name, c.port_name].filter(Boolean) as string[];
  if (c.speed != null) parts.push(formatSpeed(c.speed));
  return parts.join(" · ") || null;
}

export function WhereCell({ device }: { device: NetworkDevice }) {
  const text = whereText(device);
  if (!text) return <Text c="dimmed" component="span">-</Text>;
  const Icon = device.connection?.type === "wireless" ? Wifi : Cable;
  return (
    <Group gap={6} wrap="nowrap">
      <Icon size={14} style={{ color: "var(--mantine-color-dimmed)", flexShrink: 0 }} />
      <Text fz="sm" component="span">{text}</Text>
    </Group>
  );
}

/** Make a live device's address static — POST the static lease, toast, and
 *  refresh the devices list. `making` is the MAC in flight, for the row's
 *  "Making static…" state and the dialog's spinner. */
export function useMakeStatic(onDone?: () => void) {
  const queryClient = useQueryClient();
  const [making, setMaking] = useState<string | null>(null);

  const makeStatic = async (device: NetworkDevice, ipAddress: string) => {
    setMaking(device.mac_address);
    try {
      await tenantApi.createStaticLease({
        ip_address: ipAddress,
        mac_address: device.mac_address,
        hostname: device.hostname || "",
      });
      toast.success(`Made ${ipAddress} static`);
      onDone?.();
      queryClient.invalidateQueries({ queryKey: NETWORK_DEVICES_QUERY_KEY });
    } catch (err: unknown) {
      const e = err as { response?: { data?: { detail?: string } } };
      toast.error(e.response?.data?.detail || "Failed to make the address static");
    } finally {
      setMaking(null);
    }
  };

  return { making, makeStatic };
}

/** "Make static" for a live device: keep the address it already has, or pick
 *  another. The DHCP pool range is shown EITHER WAY — an address inside the
 *  pool is fine if it is free, so we inform rather than refuse. */
export function MakeStaticModal({
  device,
  pool,
  busy,
  onClose,
  onSubmit,
}: {
  device: NetworkDevice | null;
  pool: { start: string; end: string } | null;
  busy: boolean;
  onClose: () => void;
  onSubmit: (device: NetworkDevice, ipAddress: string) => void;
}) {
  const [mode, setMode] = useState<"keep" | "other">("keep");
  const [otherIp, setOtherIp] = useState("");

  // Reset per device so a previous choice doesn't leak into the next open.
  useEffect(() => {
    setMode("keep");
    setOtherIp("");
  }, [device?.mac_address]);

  const currentIp = device?.ip_address ?? "";
  const chosenIp = mode === "keep" ? currentIp : otherIp.trim();

  return (
    <Modal opened={!!device} onClose={onClose} title="Make static">
      {device && (
        <Stack gap="md">
          <Text fz="sm">
            Give <Text component="span" fw={600}>{deviceName(device)}</Text> a fixed address — it
            will keep it every time it joins the network.
          </Text>
          <Radio.Group value={mode} onChange={(v) => setMode(v as "keep" | "other")}>
            <Stack gap="xs">
              <Radio value="keep" label={`Keep its current address (${currentIp})`} />
              <Radio value="other" label="Choose another address" />
            </Stack>
          </Radio.Group>
          {mode === "other" && (
            <TextInput
              placeholder="IP Address (e.g. 10.97.1.25)"
              value={otherIp}
              onChange={(e) => setOtherIp(e.target.value)}
              ff="monospace"
            />
          )}
          {pool && (
            <Text fz="xs" c="dimmed">
              DHCP pool: {pool.start} – {pool.end}. An address inside the pool works if it is free;
              one outside it avoids ever colliding with a dynamic device.
            </Text>
          )}
          <Group justify="flex-end" gap="sm">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button loading={busy} disabled={!chosenIp} onClick={() => onSubmit(device, chosenIp)}>
              Make static
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  );
}
